import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Play, Clock, Tag, Crown } from 'lucide-react';
import Navbar from '../components/Navbar';
import { AdSlot } from '../components/AdSlot';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function thumbnailFor(videoId) {
  // YouTube serves a 480x360 mqdefault for every public video.
  return videoId ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg` : null;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function LessonsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/lessons`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Video Lessons
          </h1>
          <p className="text-[var(--text-secondary)] text-base max-w-2xl mx-auto">
            Watch a recorded teaching session, then play the exact same puzzle.
            Each lesson has a stable code (e.g. <code className="font-mono">LSN-260509</code>)
            you can call out on stream so viewers jump straight to the grid.
          </p>
        </div>

        {loading && (
          <div className="text-center text-[var(--text-secondary)] py-12">Loading lessons…</div>
        )}

        {!loading && items.length === 0 && (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
            <CardContent className="p-10 text-center">
              <Crown className="w-10 h-10 text-[var(--text-disabled)] mx-auto mb-3" />
              <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)] mb-1">
                No lessons published yet
              </h3>
              <p className="text-sm text-[var(--text-secondary)]">
                Check back soon — the host posts new YouTube-paired puzzles regularly.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Top-of-grid leaderboard ad — only renders when admin flips ads_enabled on. */}
        <AdSlot size="leaderboard" testId="ad-lessons-top" className="my-6" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((lesson) => {
            const thumb = thumbnailFor(lesson.video_id);
            return (
              <Link
                key={lesson.code}
                to={`/lessons/${lesson.code}`}
                className="block"
                data-testid={`lesson-card-${lesson.code}`}
              >
                <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] hover:border-[var(--border-strong)] transition-all overflow-hidden h-full">
                  <div className="relative aspect-video bg-[var(--background-elevated)] overflow-hidden">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={lesson.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-10 h-10 text-[var(--text-disabled)]" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs font-mono">
                      {lesson.code}
                    </div>
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                      L{lesson.difficulty_level} · {lesson.difficulty_name}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-heading font-semibold text-[var(--text-primary)] line-clamp-2 mb-1">
                      {lesson.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-[var(--text-disabled)] mb-2">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(lesson.created_at)}
                      </span>
                      {lesson.label && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" /> {lesson.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2">
                      {lesson.description || 'Watch and solve along.'}
                    </p>
                    {lesson.tags && lesson.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {lesson.tags.slice(0, 4).map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </main>
    </div>
  );
}
